import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { AlertCircle, Building2, ChevronLeft, ChevronRight, Plus, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { isNewWorkSearch, newWorkCustomerPath, isDirectWorkSearch, directWorkCustomerPath } from "@/lib/newWorkFlow";

const CUSTOMER_ACCESS_ROLES = ["admin", "admin_manager", "project_manager", "sales"];

const LIFECYCLE_STAGES = [
  { value: "prospect", label: "Prospecto" },
  { value: "customer", label: "Cliente" },
];

function lifecycleLabel(stage: string | null) {
  if (!stage) return "-";
  return LIFECYCLE_STAGES.find((s) => s.value === stage)?.label ?? stage;
}

function lifecycleBadgeVariant(stage: string | null): "default" | "secondary" | "destructive" | "outline" {
  return stage === "customer" ? "default" : stage === "prospect" ? "secondary" : "outline";
}

export default function Customers() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const journeySearch = useSearch();
  const newWork = isNewWorkSearch(journeySearch);
  const directWork = !newWork && isDirectWorkSearch(journeySearch);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [lifecycleStage, setLifecycleStage] = useState<"prospect" | "customer" | undefined>(undefined);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const canAccess = user ? CUSTOMER_ACCESS_ROLES.includes(user.role) : false;
  const permissions = trpc.customers.permissions.useQuery(undefined, { enabled: canAccess });
  const query = trpc.customers.list.useQuery(
    { search: debouncedSearch || undefined, lifecycleStage, page, pageSize },
    { enabled: canAccess },
  );

  if (!user) return null;

  if (!canAccess) {
    return (
      <DashboardLayout>
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-3 text-muted-foreground">
          <AlertCircle className="h-8 w-8" />
          <p className="text-sm">No tienes permiso para ver clientes.</p>
        </div>
      </DashboardLayout>
    );
  }

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = query.data?.totalPages ?? 1;

  return (
    <DashboardLayout>
      <div className="min-w-0 max-w-full space-y-4 p-6">
        {directWork && <p className="text-sm text-muted-foreground">Trabajo sin contrato · selecciona el cliente. Después podrás elegir o crear su sede y confirmar una avería o mantenimiento autorizado.</p>}
        {newWork && <p className="text-sm text-muted-foreground">Cliente → Contrato → Instalación. Selecciona el cliente o crea uno nuevo. Después podrás añadir una o varias instalaciones con una única economía contractual.</p>}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            <h1 className="text-xl font-semibold">{newWork ? "Nueva obra · 1. Cliente" : directWork ? "Trabajo sin contrato · Cliente" : "Clientes"}</h1>
            {total > 0 && <Badge variant="secondary">{total}</Badge>}
          </div>
          {permissions.data?.canCreateCustomer && (
            <Button onClick={() => setLocation(newWork ? "/customers/new?newWork=1" : directWork ? "/customers/new?directWork=1" : "/customers/new")}>
              <Plus className="mr-1 h-4 w-4" />
              Nuevo cliente
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8 pr-8"
              placeholder="Buscar por nombre, NIF o código"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setSearch("");
                  setDebouncedSearch("");
                  setPage(1);
                }}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Select
            value={lifecycleStage ?? "all"}
            onValueChange={(v) => {
              setLifecycleStage(v === "all" ? undefined : (v as "prospect" | "customer"));
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {LIFECYCLE_STAGES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card className="min-w-0 max-w-full overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {query.isLoading ? "Cargando" : `${total} cliente${total !== 1 ? "s" : ""}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {query.isError ? (
              <div className="flex items-center gap-2 p-6 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>Error al cargar clientes.</span>
              </div>
            ) : (
              <Table className="hidden md:table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Código cliente</TableHead>
                    <TableHead>Ref. técnica</TableHead>
                    <TableHead>Razón social</TableHead>
                    <TableHead>NIF/CIF</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Comercial</TableHead>
                    <TableHead className="text-right">Instalaciones</TableHead>
                    <TableHead className="text-right">DQ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {query.isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((__, j) => (
                          <TableCell key={j}><div className="h-4 w-3/4 rounded bg-muted" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                        {debouncedSearch || lifecycleStage ? "No hay clientes que coincidan con los filtros." : "No hay clientes. Añade el primer cliente."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => (
                      <TableRow key={item.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setLocation(newWork ? newWorkCustomerPath(item.id) : directWork ? directWorkCustomerPath(item.id) : `/customers/${item.id}`)}>
                        <TableCell className="font-mono text-xs font-medium">{item.customerCode ?? "-"}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{item.partnerCode}</TableCell>
                        <TableCell>
                          <div className="font-medium">{item.tradeName?.trim() || item.legalName}</div>
                          {item.tradeName?.trim() && item.tradeName.trim() !== item.legalName && (
                            <div className="text-xs text-muted-foreground">{item.legalName}</div>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{item.taxId ?? "-"}</TableCell>
                        <TableCell>
                          <Badge variant={lifecycleBadgeVariant(item.lifecycleStage)}>{lifecycleLabel(item.lifecycleStage)}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{item.salesOwnerName ?? "-"}</TableCell>
                        <TableCell className="text-right text-sm">{item.installationCount}</TableCell>
                        <TableCell className="text-right">
                          {item.openDqCount > 0 ? <Badge variant="destructive">{item.openDqCount}</Badge> : <span className="text-sm text-muted-foreground">0</span>}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}

            {!query.isLoading && !query.isError && items.length > 0 && (
              <div className="grid min-w-0 gap-2 p-3 md:hidden">
                {items.map((item) => (
                  <button key={item.id} className="w-full min-w-0 max-w-full overflow-hidden break-words rounded-md border p-3 text-left" onClick={() => setLocation(newWork ? newWorkCustomerPath(item.id) : directWork ? directWorkCustomerPath(item.id) : `/customers/${item.id}`)}>
                    <div className="flex min-w-0 items-start justify-between gap-2">
                      <div className="min-w-0 [overflow-wrap:anywhere]">
                        <div className="font-medium">{item.tradeName?.trim() || item.legalName}</div>
                        {item.tradeName?.trim() && item.tradeName.trim() !== item.legalName && <div className="text-sm text-muted-foreground">{item.legalName}</div>}
                        <div className="font-mono text-xs text-muted-foreground">{item.customerCode ?? "Sin código"} · {item.partnerCode}</div>
                      </div>
                      <Badge className="shrink-0" variant={lifecycleBadgeVariant(item.lifecycleStage)}>{lifecycleLabel(item.lifecycleStage)}</Badge>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground [overflow-wrap:anywhere]">
                      <span>NIF: {item.taxId ?? "-"}</span>
                      <span>Inst.: {item.installationCount}</span>
                      <span>DQ: {item.openDqCount}</span>
                      <span>{item.salesOwnerName ?? "Sin responsable"}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Página {page} de {totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
